/**
 * SCRUM-285/286/287 (épica SCRUM-279 "Nichos dinámicos") — matriz nicho ×
 * módulo (lectura) + creación de nichos componiendo módulos ya existentes
 * del catálogo (sin crear módulos nuevos: curar flags/nav_paths de un
 * módulo es un paso de negocio distinto, no de este panel) + defaults de
 * plan por nicho (límites/flags de referencia). Fuente de datos real:
 * sm_capability_modules/sm_capability_niches vía
 * /api/admin/capability-niches — nunca un snapshot hardcodeado.
 *
 * SCRUM-287 — alcance deliberado: esto solo EDITA las columnas
 * default_plan_limits/default_plan_flags del nicho (referencia comercial).
 * NO cablea estos valores como fuente real de PLAN_FLAG_TEMPLATES/
 * SM_PLAN_LIMITS en el runtime de onboarding — ver comentario en el
 * componente y el ticket para el detalle de por qué ese cutover queda
 * para un pase dedicado (toca la creación de tenants nuevos, un flujo
 * crítico de facturación).
 */
import { useEffect, useMemo, useState } from "react";
import { Check, ChevronLeft, Package, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";

/** SCRUM-287 — tiers comerciales reales (PLAN_PRICE_USD en admin-nav.ts), sin free_trial/custom (no son destino de defaults por nicho). */
const NICHE_PLAN_IDS = ["starter", "pro", "business"] as const;
type NichePlanId = (typeof NICHE_PLAN_IDS)[number];

interface CapabilityModule {
  id: string;
  name: string;
  description: string | null;
  grantsFlags: string[];
  navPaths: string[];
}

interface CapabilityNiche {
  id: string;
  name: string;
  description: string | null;
  moduleIds: string[];
  defaultPlanLimits: Record<string, unknown>;
  defaultPlanFlags: Record<string, unknown>;
}

type FormState = { id: string; name: string; description: string; moduleIds: string[] };
const EMPTY_FORM: FormState = { id: "", name: "", description: "", moduleIds: [] };

export function CapabilityNichesMatrixPanel() {
  const [modules, setModules] = useState<CapabilityModule[]>([]);
  const [niches, setNiches] = useState<CapabilityNiche[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedNiche, setSelectedNiche] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = () => {
    return apiGetJson<{ modules: CapabilityModule[]; niches: CapabilityNiche[] }>("/api/admin/capability-niches")
      .then((data) => {
        setModules(data.modules ?? []);
        setNiches(data.niches ?? []);
        setLoaded(true);
      })
      .catch((err) => {
        console.error("[CapabilityNichesMatrixPanel]", err);
        toast.error("Error al cargar la matriz de nichos");
        setLoaded(true);
      });
  };

  useEffect(() => {
    void load();
  }, []);

  const moduleById = useMemo(() => new Map(modules.map((m) => [m.id, m])), [modules]);

  const filteredNiches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return niches;
    return niches.filter((n) => n.name.toLowerCase().includes(q) || n.id.toLowerCase().includes(q));
  }, [niches, query]);

  function startCreate() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setCreating(true);
    setSelectedNiche(null);
  }

  function startEdit(niche: CapabilityNiche) {
    setCreating(false);
    setEditingId(niche.id);
    setForm({ id: niche.id, name: niche.name, description: niche.description ?? "", moduleIds: niche.moduleIds });
  }

  function cancelForm() {
    setCreating(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  function toggleModule(moduleId: string) {
    setForm((f) => ({
      ...f,
      moduleIds: f.moduleIds.includes(moduleId)
        ? f.moduleIds.filter((m) => m !== moduleId)
        : [...f.moduleIds, moduleId],
    }));
  }

  async function submitForm() {
    if (!form.name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    if (!form.moduleIds.length) {
      toast.error("Elegí al menos un módulo");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await apiSendJson(`/api/admin/capability-niches?id=${encodeURIComponent(editingId)}`, "PATCH", {
          name: form.name.trim(),
          description: form.description.trim() || null,
          moduleIds: form.moduleIds,
        });
        toast.success("Nicho actualizado");
      } else {
        if (!form.id.trim()) {
          toast.error("El id es obligatorio (ej: cafe_peluqueria_tienda)");
          setSaving(false);
          return;
        }
        await apiSendJson("/api/admin/capability-niches", "POST", {
          id: form.id.trim(),
          name: form.name.trim(),
          description: form.description.trim() || null,
          moduleIds: form.moduleIds,
        });
        toast.success("Nicho creado");
      }
      cancelForm();
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo guardar el nicho");
    } finally {
      setSaving(false);
    }
  }

  if ((creating || editingId) && loaded) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={cancelForm}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Todos los nichos
        </button>

        <h3 className="text-base font-semibold">{editingId ? `Editar "${editingId}"` : "Nuevo nicho"}</h3>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Nombre</Label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Ej: Café + Peluquería + Tienda" />
          </div>
          <div>
            <Label>Id {editingId ? "" : "(minúsculas, sin espacios)"}</Label>
            <Input
              value={editingId ?? form.id}
              onChange={(e) => setForm((f) => ({ ...f, id: e.target.value }))}
              placeholder="cafe_peluqueria_tienda"
              disabled={!!editingId}
            />
          </div>
          <div className="sm:col-span-2">
            <Label>Descripción (opcional)</Label>
            <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} />
          </div>
        </div>

        <div>
          <Label>Módulos que componen este nicho</Label>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {modules.map((mod) => (
              <label
                key={mod.id}
                className="flex cursor-pointer items-start gap-2 rounded-lg border border-border/70 bg-background/40 p-2.5 text-sm"
              >
                <Checkbox checked={form.moduleIds.includes(mod.id)} onCheckedChange={() => toggleModule(mod.id)} />
                <span>
                  <span className="font-medium">{mod.name}</span>{" "}
                  <span className="font-mono text-[10px] text-muted-foreground">{mod.id}</span>
                  {mod.description ? <span className="block text-xs text-muted-foreground">{mod.description}</span> : null}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={() => void submitForm()} disabled={saving}>
            {editingId ? "Guardar cambios" : "Crear nicho"}
          </Button>
          <Button variant="ghost" onClick={cancelForm}>Cancelar</Button>
        </div>
      </div>
    );
  }

  if (loaded && selectedNiche) {
    const niche = niches.find((n) => n.id === selectedNiche);
    if (!niche) {
      setSelectedNiche(null);
      return null;
    }
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setSelectedNiche(null)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Todos los nichos
        </button>

        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-base font-semibold leading-tight">{niche.name}</h3>
            <p className="text-xs text-muted-foreground">
              {niche.moduleIds.length} módulo{niche.moduleIds.length === 1 ? "" : "s"} · <span className="font-mono">{niche.id}</span>
            </p>
            {niche.description ? <p className="mt-1 text-xs text-muted-foreground">{niche.description}</p> : null}
          </div>
          <Button size="sm" variant="outline" onClick={() => startEdit(niche)}>Editar</Button>
        </div>

        <div className="space-y-2">
          {niche.moduleIds.map((moduleId) => {
            const mod = moduleById.get(moduleId);
            return (
              <div key={moduleId} className="rounded-lg border border-border/70 bg-background/40 p-3">
                <div className="flex items-center gap-2">
                  <Package className="h-3.5 w-3.5 text-primary" />
                  <span className="text-sm font-medium">{mod?.name ?? moduleId}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">{moduleId}</span>
                </div>
                {mod?.description ? <p className="mt-1 text-xs text-muted-foreground">{mod.description}</p> : null}
                {mod && mod.navPaths.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {mod.navPaths.map((path) => (
                      <span key={path} className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                        {path}
                      </span>
                    ))}
                  </div>
                ) : null}
                {mod && mod.grantsFlags.length === 0 ? (
                  <p className="mt-1.5 text-[10px] italic text-muted-foreground">
                    Sin flags curados todavía — no restringe entitlement, solo aplicabilidad de nav.
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>

        <NichePlanDefaultsEditor niche={niche} onSaved={() => void load()} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <p className="max-w-2xl text-sm text-muted-foreground">
          Cada nicho es un bundle de módulos de capacidad ya existentes en el catálogo. Creá uno nuevo
          componiendo módulos, o entrá a uno para ver el detalle y editarlo.
        </p>
        <div className="flex gap-2">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filtrar nicho…"
            className="h-9 w-full rounded-lg border border-border bg-background px-3 text-sm sm:w-56"
          />
          <Button size="sm" onClick={startCreate}>
            <Plus className="mr-1.5 h-4 w-4" /> Nuevo nicho
          </Button>
        </div>
      </div>

      {!loaded ? (
        <p className="text-sm text-muted-foreground">Cargando matriz…</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {filteredNiches.map((niche) => (
            <button
              key={niche.id}
              type="button"
              onClick={() => setSelectedNiche(niche.id)}
              className="rounded-xl border border-border bg-card/40 p-4 text-left shadow-sm transition-colors hover:border-primary/40 hover:bg-card/70"
            >
              <h3 className="text-sm font-semibold leading-tight">{niche.name}</h3>
              <p className="mt-1 text-[11px] tabular-nums text-muted-foreground">
                {niche.moduleIds.length} módulo{niche.moduleIds.length === 1 ? "" : "s"} · <span className="font-mono">{niche.id}</span>
              </p>
              <div className="mt-2 flex flex-wrap gap-1">
                {niche.moduleIds.slice(0, 4).map((mId) => (
                  <span key={mId} className="flex items-center gap-0.5 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary">
                    <Check className="h-2.5 w-2.5" /> {moduleById.get(mId)?.name ?? mId}
                  </span>
                ))}
                {niche.moduleIds.length > 4 ? (
                  <span className="text-[10px] text-muted-foreground">+{niche.moduleIds.length - 4} más</span>
                ) : null}
              </div>
            </button>
          ))}
        </div>
      )}

      {loaded && filteredNiches.length === 0 ? (
        <p className="text-sm text-muted-foreground">Ningún nicho coincide con el filtro.</p>
      ) : null}
    </div>
  );
}

/**
 * SCRUM-287 — defaults de plan de REFERENCIA para este nicho, por plan
 * (starter/pro/business). Guarda en default_plan_limits[plan] /
 * default_plan_flags[plan] (JSONB, ya existente desde SCRUM-281).
 *
 * IMPORTANTE — alcance deliberadamente acotado: esto es la plantilla
 * comercial de referencia para cuando se materialice un tenant nuevo de
 * este nicho. NO es el gate runtime — `productFlags` materializados en
 * cada tenant siguen siendo la única autoridad de entitlement (invariante
 * ADR-006 #11), y el flujo de onboarding real (business-presets.ts →
 * PLAN_FLAG_TEMPLATES) todavía no lee de acá — cablear esa lectura es un
 * cambio en el flujo de alta de tenants (código de facturación/entitlement
 * en producción) que merece su propio pase dedicado y probado, no una
 * extensión de este formulario. Cambiar un valor acá hoy no tiene efecto
 * visible hasta que ese cutover se haga — documentado también en Jira.
 */
function NichePlanDefaultsEditor({
  niche,
  onSaved,
}: {
  niche: CapabilityNiche;
  onSaved: () => void;
}) {
  const [plan, setPlan] = useState<NichePlanId>("starter");
  const [limitsText, setLimitsText] = useState("{}");
  const [flagsText, setFlagsText] = useState("");
  const [saving, setSaving] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    const limitsForPlan = (niche.defaultPlanLimits as Record<string, unknown>)?.[plan];
    setLimitsText(JSON.stringify(limitsForPlan ?? {}, null, 2));
    const flagsForPlan = (niche.defaultPlanFlags as Record<string, unknown>)?.[plan];
    setFlagsText(Array.isArray(flagsForPlan) ? flagsForPlan.join(", ") : "");
    setJsonError(null);
  }, [niche, plan]);

  async function save() {
    let parsedLimits: unknown;
    try {
      parsedLimits = JSON.parse(limitsText);
    } catch {
      setJsonError("Los límites deben ser JSON válido, ej: { \"products\": 200, \"locations\": 2 }");
      return;
    }
    setJsonError(null);
    setSaving(true);
    try {
      const nextLimits = { ...(niche.defaultPlanLimits as Record<string, unknown>), [plan]: parsedLimits };
      const nextFlags = {
        ...(niche.defaultPlanFlags as Record<string, unknown>),
        [plan]: flagsText.split(",").map((f) => f.trim()).filter(Boolean),
      };
      await apiSendJson(`/api/admin/capability-niches?id=${encodeURIComponent(niche.id)}`, "PATCH", {
        defaultPlanLimits: nextLimits,
        defaultPlanFlags: nextFlags,
      });
      toast.success(`Defaults de plan "${plan}" guardados`);
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-lg border border-dashed border-border/70 p-3">
      <p className="text-xs font-semibold text-foreground">Defaults de plan por nicho (referencia)</p>
      <p className="mt-0.5 text-[11px] text-muted-foreground">
        Plantilla comercial para cuando se cree un tenant nuevo de este nicho — todavía no cablea al
        runtime de entitlement (ver comentario en el código / Jira SCRUM-287).
      </p>

      <div className="mt-2 flex gap-1.5">
        {NICHE_PLAN_IDS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPlan(p)}
            className={`rounded-md px-2.5 py-1 text-xs font-medium capitalize ${
              plan === p ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            {p}
          </button>
        ))}
      </div>

      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <div>
          <Label className="text-xs">Límites (JSON)</Label>
          <Textarea
            value={limitsText}
            onChange={(e) => setLimitsText(e.target.value)}
            rows={4}
            className="font-mono text-xs"
          />
          {jsonError ? <p className="mt-1 text-[11px] text-destructive">{jsonError}</p> : null}
        </div>
        <div>
          <Label className="text-xs">Flags a habilitar (separados por coma)</Label>
          <Textarea
            value={flagsText}
            onChange={(e) => setFlagsText(e.target.value)}
            rows={4}
            placeholder="dine_in_tables, table_reservations"
            className="font-mono text-xs"
          />
        </div>
      </div>

      <Button size="sm" className="mt-2" onClick={() => void save()} disabled={saving}>
        Guardar defaults de {plan}
      </Button>
    </div>
  );
}
